import { CONFIG_VERSION, defaultActionButtonStyle, DEFAULT_ADDRESS_HEADING_TEXT, defaultCoverage, defaultDiscount, defaultFieldsStyle, defaultHeader, defaultMessages, defaultNewsletterStyle, defaultProductCard, defaultShippingMethod, defaultSubmitStyle, defaultTermsStyle, defaultThankYou, defaultTotalsStyle, defaultTracking, } from './schema.js';
/** Build a new shipping rate with a unique id and defaulted optional attributes. */
export function makeShippingRate(overrides = {}) {
    return {
        id: `sr_${crypto.randomUUID()}`,
        name: 'Nuevo metodo de envio',
        description: '',
        price: 0,
        conditions: [],
        country_only: false,
        countries: [],
        ...overrides,
    };
}
/** Build a field with the optional attributes defaulted, so callers stay terse. */
function field(f) {
    return {
        placeholder: '',
        show_icon: true,
        min_length: 0,
        max_length: 0,
        error_text: '',
        custom: false,
        connect_to: '',
        options: [],
        allow_only_numbers: false,
        prefix_text: '',
        ...f,
    };
}
/**
 * The "Quantity offers" field: a non-input block that renders the matching quantity offer
 * at its position in the form. It lives in `fields` so it reorders and shows/hides exactly
 * like every other field. Used both as a default-config entry and as the backfill entry for
 * configs saved before this field existed.
 */
export const quantityOffersFieldKey = 'quantity_offers';
export const defaultQuantityOffersFieldEntry = field({
    key: quantityOffersFieldKey,
    label: 'Ofertas por cantidad',
    type: 'quantity_offers',
    required: false,
    icon: 'none',
    enabled: true,
});
/**
 * The "block" fields: non-input sections that render at their position in the form. Each is a
 * field so it reorders and shows/hides like any other, while its rich styling/text lives in
 * the matching top-level config key (`product_card`, `totals_style`, ...). These double as the
 * backfill entries for configs saved before the sections were modeled as fields.
 */
export const defaultOrderSummaryFieldEntry = field({
    key: 'order_summary',
    label: 'Resumen del pedido',
    type: 'order_summary',
    required: false,
    icon: 'none',
    enabled: true,
});
export const defaultDiscountCodeFieldEntry = field({
    key: 'discount_code',
    label: 'Codigo de descuento',
    type: 'discount_code',
    required: false,
    icon: 'none',
    enabled: false,
});
export const defaultTotalsSummaryFieldEntry = field({
    key: 'totals_summary',
    label: 'Resumen de totales',
    type: 'totals_summary',
    required: false,
    icon: 'none',
    enabled: true,
});
export const defaultShippingMethodFieldEntry = field({
    key: 'shipping_method',
    label: 'Metodo de envio',
    type: 'shipping_method',
    required: false,
    icon: 'none',
    enabled: true,
});
/**
 * The address heading, as a **custom text field** — the block type it used to be is retired.
 *
 * 🚨 It is a `custom_text` entry like any the merchant could add themselves, which is the whole
 * point: `address_heading` was a singleton block whose entire config was one string rendered as
 * centred bold text, and `custom_text` already does that with the alignment, size, weight and
 * colour under the merchant's control. So this ships with the same wording and the same look
 * (18px bold centred, the default `appearance.text_color`) and is then fully editable — and
 * deletable, which the block never was.
 *
 * The key is FIXED rather than the usual `cf_<uuid>`: `defaultFormConfig` is a module constant, so
 * a random id would differ between processes and make two "identical" default configs compare
 * unequal (`dashboard.model.ts` diffs exactly that). The `cf_` prefix keeps it in the custom-field
 * namespace, and `migrateAddressHeadingField` reuses it when converting a stored block.
 */
export const addressHeadingFieldKey = 'cf_address_heading';
export const defaultAddressHeadingFieldEntry = {
    ...field({
        key: addressHeadingFieldKey,
        label: 'Texto personalizado',
        type: 'custom_text',
        required: false,
        icon: 'none',
        enabled: true,
    }),
    custom: true,
    text_block: {
        content: DEFAULT_ADDRESS_HEADING_TEXT,
        align: 'center',
        font_size: 18,
        font_weight: 'bold',
        // The old block inherited `appearance.text_color`; `custom_text` states a colour, so this is
        // the default appearance value. A merchant who changed their palette will want to change this
        // too — which they now can, and could not before.
        text_color: '#1a1a1a',
    },
};
/**
 * Form-chrome block fields: the newsletter opt-in, the terms consent line, and the submit
 * button. Each reorders/shows-hides like any field, with its text/styling in the matching
 * top-level key (`consent.newsletter`, `consent.terms`, `submit`). `submit` is always rendered
 * (the designer hides its enable toggle), so its `enabled` stays true. These double as backfill
 * entries for configs saved before these were modeled as fields.
 */
export const defaultNewsletterFieldEntry = field({
    key: 'newsletter',
    label: 'Newsletter',
    type: 'newsletter',
    required: false,
    icon: 'none',
    enabled: true,
});
export const defaultTermsFieldEntry = field({
    key: 'terms',
    label: 'Terminos y condiciones',
    type: 'terms',
    required: false,
    icon: 'none',
    enabled: true,
});
export const defaultSubmitFieldEntry = field({
    key: 'submit',
    label: 'Boton de envio',
    type: 'submit',
    required: false,
    icon: 'none',
    enabled: true,
});
/**
 * The field types a merchant can add from the designer's "Add custom field, button or image"
 * modal. Drives the modal list and `customFieldDefaults`. Order is the modal display order.
 */
export const customFieldTypes = [
    'custom_text',
    'custom_image',
    'whatsapp_button',
    'text',
    'dropdown',
    'single_choice',
    'checkbox',
    'date',
    'link_button',
];
/**
 * Build a default entry for a merchant-added custom field. `custom: true` marks it deletable
 * and routed to the extended editor; the key is unique so it can never collide. Each type
 * seeds the matching inline config and a neutral Spanish label the merchant edits afterwards.
 */
export function customFieldDefaults(type) {
    const base = field({
        key: `cf_${crypto.randomUUID()}`,
        label: 'Campo personalizado',
        type,
        required: false,
        icon: 'none',
        enabled: true,
    });
    base.custom = true;
    switch (type) {
        case 'custom_text':
            return {
                ...base,
                label: 'Texto personalizado',
                text_block: {
                    content: 'Tu texto personalizado',
                    align: 'center',
                    font_size: 16,
                    font_weight: 'bold',
                    text_color: '#000000',
                },
            };
        case 'custom_image':
            return { ...base, label: 'Imagen o GIF', image: { url: '', image_size: 100 } };
        case 'whatsapp_button':
            return {
                ...base,
                label: 'Comprar por WhatsApp',
                whatsapp: {
                    text: 'Comprar por WhatsApp',
                    subtitle: '',
                    phone: '',
                    create_order: false,
                    message: 'Hola! Quiero comprar este producto: {page_url}',
                    style: { ...defaultActionButtonStyle },
                },
            };
        case 'link_button':
            return {
                ...base,
                label: 'Boton de enlace',
                link: {
                    text: 'Ver mas',
                    subtitle: '',
                    url: '',
                    style: { ...defaultActionButtonStyle, bg_color: '#000000' },
                },
            };
        case 'text':
            return {
                ...base,
                label: 'Tu campo',
                placeholder: 'Tu campo',
                min_length: 0,
                max_length: 250,
            };
        case 'dropdown':
            return {
                ...base,
                label: 'Lista desplegable',
                placeholder: 'Selecciona una opcion',
                options: ['Opcion 1', 'Opcion 2', 'Opcion 3'],
            };
        case 'single_choice':
            return {
                ...base,
                label: 'Eleccion unica',
                options: ['Opcion 1', 'Opcion 2', 'Opcion 3'],
            };
        case 'checkbox':
            return { ...base, label: 'Acepto' };
        case 'date':
            // The placeholder is the empty state of a picker CONTROL, not the prompt of a text input:
            // both bundles render a calendar the buyer taps (`form-tree/src/datepicker.ts` and
            // `form-renderer`'s `renderDateField`), and this is what the control shows until they pick.
            // The FORMAT rather than an instruction, so the shape of the value is legible before and
            // after. `DATE_PLACEHOLDER` in `@cod/form-tree/pure` is the same string, applied when a
            // merchant clears this one — kept as a literal here because `config-schema` sits UNDER
            // `form-tree` in the dependency graph.
            return { ...base, label: 'Fecha', placeholder: 'dd/mm/aaaa' };
        default:
            return base;
    }
}
/**
 * Default form configuration. All merchant-facing strings are authored in Spanish,
 * the storefront default language. Merchants can override every visible string.
 */
export const defaultFormConfig = {
    version: CONFIG_VERSION,
    enabled: true,
    mode: 'popup',
    visibility: 'product',
    // Server-populated at serve time from the Store row; never authored by a merchant.
    store_country: null,
    // Server-derived at serve time. 'legacy' is the safe default: it keeps the bundle that runs on
    // every theme today, rather than standing it down for an SDK app that may not execute.
    storefront_runtime: 'legacy',
    cart_mode: 'product_only',
    header: { ...defaultHeader },
    button: {
        text: 'Comprar con pago contra entrega',
        bg_color: '#000000',
        text_color: '#ffffff',
        border_radius: 8,
        border_width: 1,
        border_color: '#000000',
        icon: 'bag',
        placement: 'after_buy_button',
    },
    appearance: {
        border_radius: 8,
        primary_color: '#000000',
        bg_color: '#ffffff',
        text_color: '#1a1a1a',
        accent_color: '#00c2cb',
    },
    fields_style: { ...defaultFieldsStyle },
    fields: [
        // Block fields first (order matches the historical fixed render order), then the offers
        // block and the input fields. The merchant reorders any of them in the designer.
        { ...defaultOrderSummaryFieldEntry },
        { ...defaultDiscountCodeFieldEntry },
        { ...defaultTotalsSummaryFieldEntry },
        { ...defaultShippingMethodFieldEntry },
        // The address heading — a `custom_text` field now, not a block type. Same position, same
        // wording, same look; the merchant can restyle or delete it like any other custom field.
        {
            ...defaultAddressHeadingFieldEntry,
            text_block: { ...defaultAddressHeadingFieldEntry.text_block },
        },
        { ...defaultQuantityOffersFieldEntry },
        field({
            key: 'first_name',
            label: 'Nombre',
            type: 'text',
            required: true,
            icon: 'user',
            enabled: true,
        }),
        field({
            key: 'last_name',
            label: 'Apellido',
            type: 'text',
            required: true,
            icon: 'user',
            enabled: true,
        }),
        field({
            key: 'phone',
            label: 'Telefono',
            type: 'tel',
            required: true,
            icon: 'phone',
            enabled: true,
        }),
        field({
            key: 'address',
            label: 'Direccion',
            type: 'text',
            required: true,
            icon: 'pin',
            enabled: true,
        }),
        field({
            key: 'address_2',
            label: 'Direccion 2',
            type: 'text',
            required: false,
            icon: 'pin',
            enabled: false,
        }),
        field({
            key: 'province',
            label: 'Provincia',
            type: 'text',
            required: false,
            icon: 'pin',
            enabled: false,
        }),
        field({
            key: 'city',
            label: 'Ciudad',
            type: 'text',
            required: true,
            icon: 'pin',
            enabled: true,
        }),
        field({
            key: 'postal_code',
            label: 'Codigo postal',
            type: 'text',
            required: false,
            icon: 'hash',
            enabled: true,
        }),
        field({
            key: 'email',
            label: 'Email',
            type: 'email',
            required: false,
            icon: 'at',
            enabled: false,
        }),
        field({
            key: 'order_note',
            label: 'Nota del pedido',
            type: 'textarea',
            required: false,
            icon: 'note',
            enabled: false,
        }),
        // Form-chrome blocks at the end (submit last), matching the historical bottom layout.
        { ...defaultNewsletterFieldEntry },
        { ...defaultTermsFieldEntry },
        { ...defaultSubmitFieldEntry },
    ],
    summary: {
        shipping_label: 'Gratis',
    },
    discount: { ...defaultDiscount },
    product_card: { ...defaultProductCard },
    totals_style: { ...defaultTotalsStyle },
    shipping_method: { ...defaultShippingMethod },
    shipping_rates: [
        {
            id: 'sr_free',
            name: 'Envio gratis',
            description: '',
            price: 0,
            conditions: [],
            country_only: false,
            countries: [],
        },
    ],
    coverage: { ...defaultCoverage },
    consent: {
        newsletter: {
            text: 'Quiero recibir novedades y ofertas',
            style: { ...defaultNewsletterStyle },
        },
        terms: {
            url: null,
            label: 'Acepto los terminos y condiciones',
            required: true,
            // Empty = fall back to `messages.required`, which the merchant already edits in one place.
            error_text: '',
            style: { ...defaultTermsStyle },
        },
    },
    submit: {
        text: 'Finalizar compra - {order_total}',
        subtitle: '',
        style: { ...defaultSubmitStyle },
    },
    messages: { ...defaultMessages },
    tracking: { ...defaultTracking },
    thank_you: { ...defaultThankYou },
    _future: {
        quantity_offers: [],
        upsells: [],
        downsells: [],
    },
};
/** Deep clone so callers never mutate the shared default object. */
export function makeDefaultConfig() {
    return structuredClone(defaultFormConfig);
}
//# sourceMappingURL=defaults.js.map